<template>
  <div v-if="isLoggedIn" class="marketing-preferences ctsform">
    <div v-if="fetchingData">
      <v-progress-circular indeterminate class="circular-progress"></v-progress-circular>
    </div>
    <div v-else>
      <div v-if="preferences && preferences.length" class="marketing-preferences-container">
        <div class="info-text acc_infomsg_default">
            <p v-if="!isNoCorrespondence">{{$t('Marketing.PreferencesPage.Marketing.Receive')}} {{$t('Marketing.PreferencesPage.Marketing.Text')}}</p>
            <p v-else>{{$t('Marketing.PreferencesPage.Suspended')}}</p>
        </div>
        <div class="categories-wrapper">
          <div class="table_head">
            <span class="title-text">{{$t('Marketing.Settings.Preferences.NotifyMe')}}</span>
            <span class="channels">
              <span v-for="(channel, i) in supportedMarketingChannels" :key="i" class="channel">{{channel}}</span>
            </span>
          </div>
          <div class="category notifications-holder">
            <div class="channel">
              <span class="offer-name">{{$t('Marketing.PreferencesPage.Marketing.Text')}}</span>
              <span class="offer-channels">
                <v-checkbox
                  :class="[{'disabled': isNoCorrespondence}]"
                  :disabled="isNoCorrespondence"
                  v-for="(pref, i) in preferences" :key="i"
                  v-model="pref.isactive"
                  @change="editMarketingPreferences({
                    ...pref,
                    isactive: pref.isactive ? true : (pref.initialValueIsActive === null ? null : false)
                  })"
                ></v-checkbox>
              </span>
            </div>
          </div>
        </div>
        <div>
          <v-btn :disabled="isNoCorrespondence || editedMarketingPreferences.length === 0 || isProcessing" class="primary-btn" color="success" @click="setMarketingPreferences()">{{$t('Marketing.PreferencesPage.Button.Save')}}</v-btn>
          <v-btn :disabled="isNoCorrespondence || isProcessing" class="secondary-btn" color="error" :title="$t('Marketing.PreferencesPage.Button.Reset.Tooltip')"  @click="reset2defaults(getMarketingDefaults)">{{$t('Marketing.PreferencesPage.Button.Reset')}}</v-btn>
        </div>
      </div>
      <div v-else>
        <p><v-icon color="blue" class="mr-1" size="16">info</v-icon>{{ $t('Notifications.PreferencesPage.NoOptionsAvailable') }}</p>
      </div>
    </div>
    <ProcessingDialog
      v-model="dialog"
      :isDialogPersistent="true"
      :isProcessing="isProcessing"
      :isSuccess="!transactionError"
      :processingTitle="$t('Marketing.Preferences.Dialog.Processing.Title')"
      :processingText="$t('Marketing.Preferences.Dialog.Processing.Text')"
      :successText="$t('Marketing.Preferences.Dialog.Success.Text')"
      :errorTitle="$t('Marketing.Preferences.Dialog.Error.Title')"
      :closeBtnText="$t('ProcessingDialog.CloseButtonText')"
    >
      <div slot="text-error">
        <p v-if="brandKey !=='sb'" class="generic-icon-note"><v-icon color="error" class="mr-1">error</v-icon><span>{{ $t('Marketing.Preferences.Dialog.Error.Text') }}</span></p>
        <p class="response-error">{{ transactionError }}</p>
        <p v-html="$t('Marketing.Preferences.Dialog.Error.Text.ContactSupport')"></p>
      </div>
    </ProcessingDialog>
  </div>
</template>
<script>
import lib from '@/library'
import store from '@/store'
import Branding from '@/components/mixins/Branding'
import ProcessingDialog from '@/components/common/ProcessingDialog'
export default {
  mixins: [ Branding ],
  components: {
    ProcessingDialog
  },
  data () {
    return {
      fetchingData: false,
      transactionError: null,
      isProcessing: false,
      dialog: false,
      preferences: false,
      editedMarketingPreferences: [],
      finalMarketingDefaults: []
    }
  },
  computed: {
    isLoggedIn () {
      return store.getters['isLoggedIn']
    },
    isNoCorrespondence () {
      // customer level information, will always be either true/false for all channels but should look at this globally for customer not per channel
      return this.preferences.every(n => n.isnocorrespondence)
    },
    supportedMarketingChannels () {
      return this.preferences.map(c => c.description)
    },
    getMarketingDefaults () {
      // loop through all channels and take ischanneloptedin value and assign it to the isactive value
      return this.preferences.map(pref => {
        if (pref.ischanneloptedin !== null) {
          return {
            ...pref,
            isactive: pref.ischanneloptedin
          }
        }
      })
    }
  },
  methods: {
    async fetchMarketingPreferences () {
      this.fetchingData = true
      try {
        const response = await lib.rpcService.callBroker('iw', 'getmarketingpreferences')
        if (response && response.result) {
          this.preferences = response.result.map(pref => {
            return {...pref, initialValueIsActive: pref.isactive}
          })
        }
      } catch (e) {
        console.log(e.message)
      } finally {
        this.fetchingData = false
      }
    },
    async setMarketingPreferences () {
      try {
        this.isProcessing = true
        this.dialog = true
        this.transactionError = null
        await lib.rpcService.callBroker('iw', 'setmarketingpreferences', {
          'channels': this.editedMarketingPreferences
        })
        this.editedMarketingPreferences = []
        await this.fetchMarketingPreferences()
      } catch (e) {
        console.log(e.message)
        this.transactionError = e.message || this.$t('Server.ErrorMsg.Generic')
      } finally {
        this.isProcessing = false
      }
    },
    editMarketingPreferences (editedPreference) {
      // Exctract everything except initialValueIsActive & isnocorrespondence into finalEditedPreference
      const { initialValueIsActive, isnocorrespondence, ...finalEditedPreference } = editedPreference
      const index = this.editedMarketingPreferences.findIndex(p => p.idmmmarketingchannel === finalEditedPreference.idmmmarketingchannel)
      if (index > -1) {
        this.editedMarketingPreferences.splice(index, 1)
      }
      // Only push edited array if isactive is different from initial isactive state
      if (finalEditedPreference.isactive !== initialValueIsActive) {
        this.editedMarketingPreferences.push(finalEditedPreference)
      }
    },
    async reset2defaults (systemDefaultPreferences) {
      let finalMarketingObject
      systemDefaultPreferences.forEach(m => {
        finalMarketingObject = {
          'idmmmarketingchannel': m.idmmmarketingchannel,
          'description': m.description,
          'isactive': m.isactive
        }
        this.finalMarketingDefaults.push(finalMarketingObject)
      })
      try {
        this.isProcessing = true
        this.dialog = true
        this.transactionError = null
        await lib.rpcService.callBroker('iw', 'setmarketingpreferences', {
          'channels': this.finalMarketingDefaults
        })
        this.finalMarketingDefaults = []
        await this.fetchMarketingPreferences()
      } catch (e) {
        console.log(e.message)
        this.transactionError = e.message || this.$t('Server.ErrorMsg.Generic')
      } finally {
        this.isProcessing = false
      }
    }
  },
  mounted () {
    this.fetchMarketingPreferences()
  },
  watch: {}
}
</script>

<style lang="stylus" scoped>
  @import '~@/css/config'
  @import '~@/css/mixins'
  .marketing-preferences
    text-align center 
    cursor default
    margin-bottom 20px
    // @media mobile-big-and-below
    //   margin-top 20px
    .info-text
      padding 10px 
      background #E5E5E5
      color #000
      font-weight 400
      font-size 14px
      line-height 20px
      border-radius 5px
      margin 10px 0
      text-align left
      @media mobile-big-and-below
        margin 0
    .categories-wrapper
      display flex
      flex-direction column
      .table_head
        margin 20px 0
        padding 10px 5px 10px 10px
        background #e5e5e5
        color rgba(0, 0, 0, .8)
        font-size 16px
        font-family 'Open Sans Bold'
        display flex
        .title-text
          flex 6
          text-align left
        .channels
          display flex
          width 195px
          @media mobile-big-and-below
            width 140px
        .channel
          flex 1
          text-transform uppercase
      .notifications-holder
        padding-left 10px
        padding-bottom 15px
        .category-name
          text-align left
          margin 0
          border-bottom: solid 1px #e2e2e2
          font-size 14px
          font-family 'Open Sans Bold'
        .channel
          display flex
          padding-top 5px
          line-height normal
          .offer-name
            flex 5
            text-align left
            @media mobile-big-and-above
              min-height 24px
          .offer-channels
            display flex
            width 195px          
            @media mobile-big-and-below
              width 140px
            >>> .v-input--checkbox
              margin 0 10px
              padding 0
              flex 1
              justify-content center
              align-items center
              .v-input__slot
                margin 0
                padding 0
                input
                  min-height unset
              .v-messages.theme--light
                display none
              &.disabled
                .v-input__control
                  // display none
                  opacity .5
                  input[disabled]
                    opacity 0 !important
    @media mobile-big-and-below
      .secondary-btn
        margin-left 0
        margin-right 0
</style>



// WEBPACK FOOTER //
// src/components/account/IntegratedWallet/MarketingPreferences.vue